import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import './App.css';

/* ==========================================
   THEME DEFINITIONS
   ========================================== */
const themes = [
  {
    name: 'Brown & Gold',
    key: 'brown',
    swatch: ['#3B2314', '#C8A45C', '#FDF6EC'],
    vars: {
      '--deep-brown': '#3B2314',
      '--warm-brown': '#5C3A1E',
      '--rich-brown': '#6B4226',
      '--leather': '#8B5E3C',
      '--antique-gold': '#C8A45C',
      '--gold-light': '#D4B96A',
      '--gold-shimmer': '#E8D5A3',
      '--cream': '#FDF6EC',
      '--warm-white': '#FAF3E8',
      '--parchment': '#F0E6D3',
      '--light-tan': '#E8DCC8',
      '--muted-brown': '#9B8B7A',
      '--text-dark': '#2C1A0E',
      '--text-body': '#4A3728',
      '--text-light': '#7A6B5D',
      '--shadow-sm': '0 2px 8px rgba(59,35,20,0.08)',
      '--shadow-md': '0 4px 20px rgba(59,35,20,0.12)',
      '--shadow-lg': '0 8px 40px rgba(59,35,20,0.16)',
      '--shadow-gold': '0 4px 20px rgba(200,164,92,0.2)',
    },
  },
  {
    name: 'Navy & Gold',
    key: 'navy',
    swatch: ['#1A2744', '#C8A45C', '#F5F7FC'],
    vars: {
      '--deep-brown': '#1A2744',
      '--warm-brown': '#1E3160',
      '--rich-brown': '#243875',
      '--leather': '#2E4A8A',
      '--antique-gold': '#C8A45C',
      '--gold-light': '#D4B96A',
      '--gold-shimmer': '#E8D5A3',
      '--cream': '#F5F7FC',
      '--warm-white': '#EEF1FA',
      '--parchment': '#E4E9F5',
      '--light-tan': '#D6DDF0',
      '--muted-brown': '#7A89AA',
      '--text-dark': '#0D1528',
      '--text-body': '#2A3A5C',
      '--text-light': '#5A6A8C',
      '--shadow-sm': '0 2px 8px rgba(26,39,68,0.08)',
      '--shadow-md': '0 4px 20px rgba(26,39,68,0.12)',
      '--shadow-lg': '0 8px 40px rgba(26,39,68,0.16)',
      '--shadow-gold': '0 4px 20px rgba(200,164,92,0.2)',
    },
  },
  {
    name: 'Forest & Gold',
    key: 'forest',
    swatch: ['#1A3A2A', '#C8A45C', '#F4FAF6'],
    vars: {
      '--deep-brown': '#1A3A2A',
      '--warm-brown': '#1E4A35',
      '--rich-brown': '#235940',
      '--leather': '#2A6B4D',
      '--antique-gold': '#C8A45C',
      '--gold-light': '#D4B96A',
      '--gold-shimmer': '#E8D5A3',
      '--cream': '#F4FAF6',
      '--warm-white': '#EAF5EE',
      '--parchment': '#DCEEE3',
      '--light-tan': '#CCE4D5',
      '--muted-brown': '#6A8A76',
      '--text-dark': '#0D2018',
      '--text-body': '#1E3D2A',
      '--text-light': '#4A6A56',
      '--shadow-sm': '0 2px 8px rgba(26,58,42,0.08)',
      '--shadow-md': '0 4px 20px rgba(26,58,42,0.12)',
      '--shadow-lg': '0 8px 40px rgba(26,58,42,0.16)',
      '--shadow-gold': '0 4px 20px rgba(200,164,92,0.2)',
    },
  },
  {
    name: 'Charcoal & Silver',
    key: 'charcoal',
    swatch: ['#1C1C1E', '#A8A8B0', '#F8F8F8'],
    vars: {
      '--deep-brown': '#1C1C1E',
      '--warm-brown': '#2C2C2E',
      '--rich-brown': '#3A3A3C',
      '--leather': '#4A4A4E',
      '--antique-gold': '#A8A8B0',
      '--gold-light': '#BCBCC4',
      '--gold-shimmer': '#D8D8E0',
      '--cream': '#F8F8F8',
      '--warm-white': '#F0F0F2',
      '--parchment': '#E4E4E8',
      '--light-tan': '#D8D8DC',
      '--muted-brown': '#8A8A92',
      '--text-dark': '#0A0A0C',
      '--text-body': '#2A2A30',
      '--text-light': '#5A5A64',
      '--shadow-sm': '0 2px 8px rgba(0,0,0,0.08)',
      '--shadow-md': '0 4px 20px rgba(0,0,0,0.12)',
      '--shadow-lg': '0 8px 40px rgba(0,0,0,0.16)',
      '--shadow-gold': '0 4px 20px rgba(168,168,176,0.2)',
    },
  },
];

/* ==========================================
   DESIGN STYLES, HERO LAYOUTS, FONT STYLES
   ========================================== */
const designStyles = [
  { key: 'classic', label: 'Classic', icon: '▣', desc: 'Sharp edges, traditional' },
  { key: 'modern',  label: 'Modern',  icon: '◉', desc: 'Rounded, soft shadows' },
  { key: 'minimal', label: 'Minimal', icon: '▱', desc: 'Clean lines, minimal borders' },
  { key: 'bold',    label: 'Bold',    icon: '◼', desc: 'Strong borders, offset shadows' },
];

const heroLayouts = [
  { key: 'split',    label: 'Split',    icon: '⬛⬜', desc: 'Photo right, text left' },
  { key: 'centered', label: 'Centered', icon: '⬜⬛⬜', desc: 'Circular photo, centered text' },
  { key: 'fullbg',   label: 'Full BG',  icon: '🖼', desc: 'Photo as background overlay' },
];

const fontStyles = [
  { key: 'serif',   label: 'Serif',    desc: 'Playfair + Cormorant (elegant)', vars: { '--font-display': "'Playfair Display', serif", '--font-heading': "'Cormorant Garamond', serif", '--font-body': "'Libre Franklin', sans-serif" } },
  { key: 'modern',  label: 'Modern',   desc: 'Inter throughout (clean)', vars: { '--font-display': "'Inter', sans-serif", '--font-heading': "'Inter', sans-serif", '--font-body': "'Inter', sans-serif" } },
  { key: 'slab',    label: 'Slab',     desc: 'Roboto Slab (bold & readable)', vars: { '--font-display': "'Roboto Slab', serif", '--font-heading': "'Roboto Slab', serif", '--font-body': "'Roboto', sans-serif" } },
  { key: 'elegant', label: 'Elegant',  desc: 'Cormorant Garamond throughout', vars: { '--font-display': "'Cormorant Garamond', serif", '--font-heading': "'Cormorant Garamond', serif", '--font-body': "'Libre Baskerville', serif" } },
];

/* ==========================================
   THEME SWITCHER COMPONENT
   ========================================== */
function ThemeSwitcher({ settings, onSettingChange }) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('color');

  const tabs = [
    { key: 'color', label: '🎨 Colors' },
    { key: 'style', label: '📐 Style' },
    { key: 'hero',  label: '🖼 Hero' },
    { key: 'font',  label: '🔤 Fonts' },
  ];

  return (
    <div className="theme-switcher">
      {open && (
        <div className="theme-panel">
          <div className="theme-tabs">
            {tabs.map(t => (
              <button key={t.key} className={`theme-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === 'color' && (
            <div className="theme-options-list">
              <p className="theme-panel-title">Color Theme</p>
              {themes.map(theme => (
                <button key={theme.key} className={`theme-option ${settings.color === theme.key ? 'active' : ''}`}
                  onClick={() => onSettingChange('color', theme)}>
                  <div className="theme-swatches">
                    {theme.swatch.map((c, i) => <span key={i} className="swatch" style={{ background: c }} />)}
                  </div>
                  <span className="theme-name">{theme.name}</span>
                  {settings.color === theme.key && <span className="theme-check">✓</span>}
                </button>
              ))}
            </div>
          )}

          {activeTab === 'style' && (
            <div className="theme-options-list">
              <p className="theme-panel-title">Design Style</p>
              {designStyles.map(s => (
                <button key={s.key} className={`theme-option ${settings.style === s.key ? 'active' : ''}`}
                  onClick={() => onSettingChange('style', s.key)}>
                  <span className="option-icon">{s.icon}</span>
                  <div className="option-info">
                    <span className="theme-name">{s.label}</span>
                    <span className="option-desc">{s.desc}</span>
                  </div>
                  {settings.style === s.key && <span className="theme-check">✓</span>}
                </button>
              ))}
            </div>
          )}

          {activeTab === 'hero' && (
            <div className="theme-options-list">
              <p className="theme-panel-title">Hero Layout</p>
              {heroLayouts.map(h => (
                <button key={h.key} className={`theme-option ${settings.hero === h.key ? 'active' : ''}`}
                  onClick={() => onSettingChange('hero', h.key)}>
                  <span className="option-icon">{h.icon}</span>
                  <div className="option-info">
                    <span className="theme-name">{h.label}</span>
                    <span className="option-desc">{h.desc}</span>
                  </div>
                  {settings.hero === h.key && <span className="theme-check">✓</span>}
                </button>
              ))}
            </div>
          )}

          {activeTab === 'font' && (
            <div className="theme-options-list">
              <p className="theme-panel-title">Font Style</p>
              {fontStyles.map(f => (
                <button key={f.key} className={`theme-option ${settings.font === f.key ? 'active' : ''}`}
                  onClick={() => onSettingChange('font', f)}>
                  <div className="option-info">
                    <span className="theme-name">{f.label}</span>
                    <span className="option-desc">{f.desc}</span>
                  </div>
                  {settings.font === f.key && <span className="theme-check">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <button className="theme-toggle-btn" onClick={() => setOpen(!open)} title="Customize">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
        </svg>
      </button>
    </div>
  );
}

/* ==========================================
   SVG ICON COMPONENTS
   ========================================== */
const Icons = {
  Scale: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v18M3 7l3-4 3 4M15 7l3-4 3 4M3 7h6M15 7h6M6 7l-3 9c0 1.5 1.5 3 3 3s3-1.5 3-3L6 7zM18 7l-3 9c0 1.5 1.5 3 3 3s3-1.5 3-3L18 7z" />
    </svg>
  ),
  User: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Briefcase: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
    </svg>
  ),
  Shield: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  Home: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  FileText: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  Users: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  Building: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" />
      <line x1="8" y1="6" x2="10" y2="6" />
      <line x1="14" y1="6" x2="16" y2="6" />
      <line x1="8" y1="10" x2="10" y2="10" />
      <line x1="14" y1="10" x2="16" y2="10" />
      <line x1="8" y1="14" x2="10" y2="14" />
      <line x1="14" y1="14" x2="16" y2="14" />
    </svg>
  ),
  Award: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="7" />
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
    </svg>
  ),
  CheckCircle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  Star: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  Phone: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
    </svg>
  ),
  Mail: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  MapPin: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  Clock: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  ArrowRight: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  Image: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  Gavel: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2.5l5 5-2.5 2.5-5-5zM4 14l5 5-2.5 2.5-5-5z" />
      <path d="M14.5 7.5L8.5 13.5" />
      <line x1="2" y1="22" x2="22" y2="22" />
    </svg>
  ),
  Handshake: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.5 11H17l-4-4-3 3-3-3H2.5" />
      <path d="M3.5 15.5L7 12l3 3 4-4 3 3" />
    </svg>
  ),
  Menu: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
  X: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  LinkedIn: () => (
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
  ),
  Twitter: () => (
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
  ),
  Facebook: () => (
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
  ),
  WhatsApp: () => (
    <svg viewBox="0 0 32 32" fill="currentColor"><path d="M16 2C8.268 2 2 8.268 2 16c0 2.52.664 4.883 1.824 6.93L2 30l7.294-1.794A13.94 13.94 0 0 0 16 30c7.732 0 14-6.268 14-14S23.732 2 16 2zm6.348 19.316c-.348-.174-2.06-1.016-2.38-1.132-.32-.116-.552-.174-.784.174-.232.348-.9 1.132-1.104 1.364-.202.232-.406.26-.754.086-.348-.174-1.468-.54-2.796-1.724-1.032-.922-1.73-2.06-1.932-2.408-.202-.348-.022-.536.152-.71.156-.154.348-.406.522-.608.174-.202.232-.348.348-.58.116-.232.058-.436-.028-.61-.088-.174-.784-1.89-1.074-2.588-.284-.68-.572-.588-.784-.598l-.668-.012c-.232 0-.61.086-.928.434-.318.348-1.218 1.19-1.218 2.9 0 1.71 1.246 3.362 1.42 3.594.174.232 2.452 3.742 5.942 5.248.83.358 1.48.572 1.984.732.834.264 1.594.226 2.194.138.67-.1 2.06-.842 2.35-1.656.29-.814.29-1.512.202-1.656-.086-.144-.318-.232-.666-.406z"/></svg>
  ),
};

/* ==========================================
   SCROLL ANIMATION HOOK
   ========================================== */
/* ==========================================
   SCROLL PROGRESS BAR
   ========================================== */
function ScrollProgressBar() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const scrolled = window.scrollY;
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? (scrolled / total) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <div className="scroll-progress-bar" style={{ width: `${progress}%` }} />;
}

/* ==========================================
   BACK TO TOP BUTTON
   ========================================== */
function BackToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <button
      className={`back-to-top${visible ? ' back-to-top--visible' : ''}`}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Back to top"
    >
      ↑
    </button>
  );
}

/* ==========================================
   ANIMATED COUNTER HOOK
   ========================================== */
function useCountUp(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    const isFloat = target % 1 !== 0;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const interval = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(interval);
      } else {
        setCount(isFloat ? parseFloat(current.toFixed(1)) : Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(interval);
  }, [start, target, duration]);
  return count;
}

/* ==========================================
   TYPING ANIMATION HOOK
   ========================================== */
function useTypingEffect(words, speed = 80, pause = 1800) {
  const [display, setDisplay] = useState('');
  const [wordIdx, setWordIdx] = useState(0);
  const [typing, setTyping] = useState(true);
  useEffect(() => {
    const current = words[wordIdx];
    if (typing) {
      if (display.length < current.length) {
        const t = setTimeout(() => setDisplay(current.slice(0, display.length + 1)), speed);
        return () => clearTimeout(t);
      } else {
        const t = setTimeout(() => setTyping(false), pause);
        return () => clearTimeout(t);
      }
    } else {
      if (display.length > 0) {
        const t = setTimeout(() => setDisplay(display.slice(0, -1)), speed / 2);
        return () => clearTimeout(t);
      } else {
        setWordIdx((i) => (i + 1) % words.length);
        setTyping(true);
      }
    }
  }, [display, typing, wordIdx, words, speed, pause]);
  return display;
}

function useScrollAnimation() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
}

/* ==========================================
   NAVBAR COMPONENT
   ========================================== */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Practice Areas', href: '#practice' },
    { label: 'Experience', href: '#experience' },
    { label: 'Testimonials', href: '#testimonials' },
    { label: 'Blog', href: '#blog' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <a href="#home" className="navbar-brand">
          Advocate <span>Vijita Topno</span>
        </a>
        <ul className={`nav-links ${menuOpen ? 'open' : ''}`}>
          {navItems.map((item) => (
            <li key={item.label}>
              <a href={item.href} onClick={() => setMenuOpen(false)}>
                {item.label}
              </a>
            </li>
          ))}
        </ul>
        <button
          className="mobile-menu-btn"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? (
            <Icons.X />
          ) : (
            <>
              <span></span>
              <span></span>
              <span></span>
            </>
          )}
        </button>
      </div>
    </nav>
  );
}

/* ==========================================
   HERO SECTION
   ========================================== */
function AnimatedStat({ target, suffix, label }) {
  const [started, setStarted] = useState(false);
  const ref = React.useRef();
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setStarted(true); observer.disconnect(); }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  const count = useCountUp(target, 1600, started);
  return (
    <div className="stat-item" ref={ref}>
      <span className="stat-number">{count}{suffix}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

function HeroSection() {
  const typedText = useTypingEffect([
    'Advocate & Legal Consultant',
    'Criminal Defense Expert',
    'Family Law Specialist',
    'Property Law Advisor',
  ]);

  return (
    <section className="hero" id="home">
      <div className="hero-bg-image" style={{ backgroundImage: "url('/photo.jpeg')" }}></div>
      <div className="container">
        <div className="hero-content">
          <div className="hero-badge">
            ⚖ <span>{typedText}</span><span className="typing-cursor">|</span>
          </div>
          <h1>
            Justice Through
            <span className="gold-text">Dedication &amp; Expertise</span>
          </h1>
          <p className="hero-subtitle">
            Committed to protecting your rights with integrity, compassion, and
            unwavering legal excellence.
          </p>
          <p className="hero-description">
            With years of experience handling complex legal matters, I provide
            personalized legal counsel tailored to each client's unique
            situation. Your case deserves the highest standard of representation.
          </p>
          <div className="hero-buttons">
            <a href="#contact" className="btn-primary">
              Book a Consultation
            </a>
            <a href="#practice" className="btn-outline">
              View Practice Areas
            </a>
          </div>
          <div className="hero-stats">
            <AnimatedStat target={15} suffix="+" label="Years Experience" />
            <AnimatedStat target={500} suffix="+" label="Cases Won" />
            <AnimatedStat target={98} suffix="%" label="Success Rate" />
          </div>
        </div>
        <div className="hero-photo-wrapper">
          <div className="photo-placeholder">
            <img src="/photo.jpeg" alt="Advocate" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ==========================================
   ABOUT SECTION
   ========================================== */
function AboutSection() {
  return (
    <section className="section about-section" id="about">
      <div className="container">
        <div className="section-header animate-on-scroll">
          <span className="section-label">About Me</span>
          <h2 className="section-title">A Legacy of Legal Excellence</h2>
          <div className="section-divider"></div>
        </div>
        <div className="about-grid">
          <div className="about-photo-placeholder animate-on-scroll">
            <img src="/photo.jpeg" alt="Advocate" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div className="about-content animate-on-scroll">
            <h3>Dedicated Legal Professional Serving the Community</h3>
            <p>
              With over 15 years of experience in the legal field, I have built
              a reputation for providing exceptional legal representation across
              a wide range of practice areas. My approach combines deep legal
              knowledge with a genuine commitment to understanding each client's
              unique needs.
            </p>
            <p>
              I believe that every individual deserves access to quality legal
              counsel. Whether you're facing a complex corporate dispute, a
              family matter, or need guidance on property law, I bring the same
              level of dedication and thoroughness to every case.
            </p>
            <div className="about-highlights">
              <div className="highlight-item">
                <div className="highlight-icon">
                  <Icons.Award />
                </div>
                <span className="highlight-text">
                  Bar Council Certified Advocate
                </span>
              </div>
              <div className="highlight-item">
                <div className="highlight-icon">
                  <Icons.Scale />
                </div>
                <span className="highlight-text">
                  Supreme Court Practice
                </span>
              </div>
              <div className="highlight-item">
                <div className="highlight-icon">
                  <Icons.Users />
                </div>
                <span className="highlight-text">
                  1000+ Clients Served
                </span>
              </div>
              <div className="highlight-item">
                <div className="highlight-icon">
                  <Icons.CheckCircle />
                </div>
                <span className="highlight-text">
                  Pro Bono Commitment
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ==========================================
   PRACTICE AREAS SECTION
   ========================================== */
function PracticeSection() {
  const practices = [
    {
      icon: <Icons.Users />,
      title: 'Family Law',
      description:
        'Compassionate representation in divorce, custody, alimony, and domestic matters. Protecting families through difficult transitions.',
    },
    {
      icon: <Icons.Building />,
      title: 'Corporate Law',
      description:
        'Comprehensive business legal services including incorporation, compliance, mergers & acquisitions, and corporate governance.',
    },
    {
      icon: <Icons.Shield />,
      title: 'Criminal Defense',
      description:
        'Vigorous defense of your rights in criminal proceedings. From bail applications to trial representation and appeals.',
    },
    {
      icon: <Icons.Home />,
      title: 'Property Law',
      description:
        'Expert guidance in real estate transactions, property disputes, title verification, and land acquisition matters.',
    },
    {
      icon: <Icons.FileText />,
      title: 'Civil Litigation',
      description:
        'Strategic litigation services for civil disputes including breach of contract, torts, consumer protection, and recovery suits.',
    },
    {
      icon: <Icons.Briefcase />,
      title: 'Labour & Employment',
      description:
        'Protecting employee rights and advising employers on compliance, workplace disputes, and industrial relations.',
    },
  ];

  return (
    <section className="section practice-section" id="practice">
      <div className="container">
        <div className="section-header animate-on-scroll">
          <span className="section-label">Practice Areas</span>
          <h2 className="section-title">Areas of Legal Expertise</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">
            Comprehensive legal services tailored to meet your specific needs
            with dedication and professionalism.
          </p>
        </div>
        <div className="practice-grid">
          {practices.map((practice, index) => (
            <div
              key={index}
              className="practice-card animate-on-scroll"
              style={{ transitionDelay: `${index * 0.1}s` }}
            >
              <div className="practice-icon">{practice.icon}</div>
              <h3>{practice.title}</h3>
              <p>{practice.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ==========================================
   EXPERIENCE SECTION
   ========================================== */
function ExperienceSection() {
  const experiences = [
    {
      year: '2020 – Present',
      title: 'Senior Advocate – High Court Practice',
      description:
        'Leading complex constitutional and civil matters before the High Court. Specialized in public interest litigation and corporate disputes with a track record of landmark judgments.',
    },
    {
      year: '2015 – 2020',
      title: 'Associate Partner – ABC Law Chambers',
      description:
        'Managed a diverse portfolio of criminal defense and family law cases. Mentored junior advocates and established the firm\'s pro bono division.',
    },
    {
      year: '2012 – 2015',
      title: 'District Court Advocate',
      description:
        'Built foundational trial experience handling civil litigation, property disputes, and motor accident claims. Won over 200 cases during this period.',
    },
    {
      year: '2010 – 2012',
      title: 'Junior Advocate – XYZ & Associates',
      description:
        'Gained comprehensive experience in legal research, case preparation, and courtroom proceedings under senior advocates. Developed expertise in drafting legal documents.',
    },
  ];

  return (
    <section className="section experience-section" id="experience">
      <div className="container">
        <div className="section-header animate-on-scroll">
          <span className="section-label">Experience</span>
          <h2 className="section-title">Professional Journey</h2>
          <div className="section-divider" style={{ background: 'var(--gold-shimmer)' }}></div>
          <p className="section-subtitle">
            A career built on dedication, continuous learning, and an
            unwavering commitment to justice.
          </p>
        </div>
        <div className="experience-timeline">
          {experiences.map((exp, index) => (
            <div
              key={index}
              className="timeline-item animate-on-scroll"
              style={{ transitionDelay: `${index * 0.15}s` }}
            >
              <div className="timeline-dot"></div>
              <span className="timeline-year">{exp.year}</span>
              <h3 className="timeline-title">{exp.title}</h3>
              <p className="timeline-description">{exp.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ==========================================
   TESTIMONIALS SECTION
   ========================================== */
function TestimonialsSection() {
  const testimonials = [
    {
      text: 'An exceptional advocate who handled my property dispute with remarkable professionalism. The outcome exceeded my expectations. I highly recommend their services to anyone seeking legal representation.',
      name: 'Rajesh Kumar',
      role: 'Business Owner',
      rating: 5,
    },
    {
      text: 'During my most challenging time, this advocate provided not just legal expertise but genuine empathy and support. Their dedication to my family matter made all the difference in the world.',
      name: 'Priya Sharma',
      role: 'Healthcare Professional',
      rating: 5,
    },
    {
      text: 'Brilliant legal mind with a deep understanding of corporate law. Helped our company navigate complex regulatory challenges seamlessly. A trusted advisor we continue to rely on for all legal matters.',
      name: 'Amit Patel',
      role: 'CEO, Tech Solutions Pvt Ltd',
      rating: 5,
    },
  ];

  return (
    <section className="section testimonials-section" id="testimonials">
      <div className="container">
        <div className="section-header animate-on-scroll">
          <span className="section-label">Testimonials</span>
          <h2 className="section-title">What Clients Say</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">
            The trust and confidence of my clients is the greatest testament to
            my practice.
          </p>
        </div>
        <div className="testimonial-grid">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="testimonial-card animate-on-scroll"
              style={{ transitionDelay: `${index * 0.1}s` }}
            >
              <div className="testimonial-quote">"</div>
              <div className="testimonial-stars">
                {Array.from({ length: testimonial.rating }).map((_, i) => (
                  <Icons.Star key={i} />
                ))}
              </div>
              <p className="testimonial-text">{testimonial.text}</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">
                  <Icons.User />
                </div>
                <div>
                  <div className="testimonial-name">{testimonial.name}</div>
                  <div className="testimonial-role">{testimonial.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ==========================================
   BLOG DATA (shared between list & post page)
   ========================================== */
const blogPosts = [
  {
    slug: 'jharkhand-high-court-justice-tarlok-singh-chauhan',
    category: 'Achievements',
    title: 'A Proud Moment: Meeting Justice Tarlok Singh Chauhan at Jharkhand High Court',
    excerpt:
      'Got the opportunity to be part of a distinguished event with Jharkhand High Court Justice Tarlok Singh Chauhan Sir — an inspiring experience that reaffirms our commitment to justice...',
    date: 'February 2026',
    image: '/blog-hc4.jpg',
    content: [
      {
        type: 'paragraph',
        text: 'It was an honour and a deeply inspiring moment to be part of an event alongside the Hon\'ble Justice Tarlok Singh Chauhan of the Jharkhand High Court. Such opportunities are a reminder of why we chose this profession — to serve justice and uphold the values of our legal system.',
      },
      {
        type: 'images',
        images: ['/blog-hc1.jpeg', '/blog-hc2.jpeg', '/blog-hc3.jpeg', '/blog-hc4.jpg'],
        caption: 'With Hon\'ble Justice Tarlok Singh Chauhan, Jharkhand High Court',
      },
      {
        type: 'paragraph',
        text: 'Justice Tarlok Singh Chauhan is known for his insightful judgments and his unwavering commitment to the rule of law. Interacting with him was both humbling and motivating. His words of wisdom about the responsibilities of advocates in a democratic society will stay with me for a long time.',
      },
      {
        type: 'paragraph',
        text: 'As an advocate practising in Jamshedpur, events like these strengthen our resolve to provide honest, diligent legal representation to our clients and to continuously grow as legal professionals. I am grateful for this experience and look forward to many more such milestones in my journey.',
      },
    ],
  },
  {
    slug: 'property-disputes-rights-guide',
    category: 'Property Law',
    title: 'Understanding Your Rights in Property Disputes: A Complete Guide',
    excerpt:
      'Property disputes can be complex and emotionally draining. Here is a comprehensive overview of your legal rights and the steps you can take to protect your interests...',
    date: 'January 15, 2026',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&q=80',
    content: [
      {
        type: 'paragraph',
        text: 'Property disputes are among the most common legal issues faced by individuals and families in India. Whether it involves ancestral land, a purchased flat, or boundary disagreements with neighbours, understanding your legal rights is the first step toward resolution.',
      },
      {
        type: 'paragraph',
        text: 'Under Indian law, property rights are governed by multiple statutes including the Transfer of Property Act 1882, the Registration Act 1908, and state-specific land revenue laws. Knowing which law applies to your situation can make a significant difference in strategy and outcome.',
      },
      {
        type: 'paragraph',
        text: 'Key steps to protect your interests include: ensuring your title deed is registered, maintaining a paper trail of all transactions, and seeking legal counsel before signing any agreement. Early legal intervention often prevents disputes from escalating to full-blown litigation.',
      },
      {
        type: 'paragraph',
        text: 'If you are involved in a property dispute or want to proactively secure your property rights, feel free to reach out for a consultation. Every situation is unique and deserves personalised legal attention.',
      },
    ],
  },
  {
    slug: 'navigating-divorce-proceedings',
    category: 'Family Law',
    title: 'Navigating Divorce Proceedings: What Every Spouse Should Know',
    excerpt:
      'The legal aspects of divorce can be overwhelming. This article breaks down the process, from filing to settlement, to help you make informed decisions...',
    date: 'December 28, 2025',
    image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600&q=80',
    content: [
      {
        type: 'paragraph',
        text: 'Divorce is never an easy decision, and the legal process that follows can feel equally difficult. However, understanding the steps involved can help you approach the situation with clarity and confidence.',
      },
      {
        type: 'paragraph',
        text: 'In India, divorce can be sought under different personal laws — the Hindu Marriage Act, the Special Marriage Act, or the Dissolution of Muslim Marriages Act, depending on the religion and circumstances of the parties. Mutual consent divorces are generally faster, while contested divorces involve more stages including evidence and cross-examination.',
      },
      {
        type: 'paragraph',
        text: 'Important aspects to consider include alimony, child custody, and division of jointly held assets. Courts in India increasingly prioritise the welfare of children and aim to ensure a fair financial settlement for both parties.',
      },
      {
        type: 'paragraph',
        text: 'If you are considering or facing divorce proceedings, consult a family law advocate early. Proper legal guidance from the start protects your rights and often leads to a smoother, less contentious resolution.',
      },
    ],
  },
  {
    slug: 'compliance-requirements-indian-startups-2026',
    category: 'Corporate Law',
    title: 'New Compliance Requirements for Indian Startups in 2026',
    excerpt:
      'Recent regulatory changes have introduced new compliance obligations for startups. Here is what founders and directors need to know to stay compliant...',
    date: 'December 10, 2025',
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&q=80',
    content: [
      {
        type: 'paragraph',
        text: 'The regulatory landscape for startups in India has evolved significantly in recent years. 2026 brings fresh compliance obligations that founders and directors must be aware of to avoid penalties and ensure smooth operations.',
      },
      {
        type: 'paragraph',
        text: 'Key areas of focus include annual filings with the Registrar of Companies (ROC), GST compliance, labour law adherence for growing teams, and data protection obligations under the Digital Personal Data Protection Act 2023. Startups registered under DPIIT must also ensure they meet the eligibility criteria to retain their recognised status.',
      },
      {
        type: 'paragraph',
        text: 'Directors should be particularly vigilant about timely board meetings, maintenance of statutory registers, and filing of financial statements. Non-compliance can attract hefty penalties and even disqualification of directors.',
      },
      {
        type: 'paragraph',
        text: 'A proactive compliance calendar, combined with a trusted corporate law advisor, can save startups significant time and money. If your startup needs a compliance audit or incorporation assistance, reach out to discuss how we can help.',
      },
    ],
  },
];

/* ==========================================
   BLOG SECTION
   ========================================== */
function BlogSection() {
  const posts = blogPosts;

  return (
    <section className="section blog-section" id="blog">
      <div className="container">
        <div className="section-header animate-on-scroll">
          <span className="section-label">Legal Insights</span>
          <h2 className="section-title">Articles &amp; Resources</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">
            Stay informed with the latest legal developments and insights from
            my practice.
          </p>
        </div>
        <div className="blog-grid">
          {posts.map((post, index) => (
            <div
              key={index}
              className="blog-card animate-on-scroll"
              style={{ transitionDelay: `${index * 0.1}s` }}
            >
              <div className="blog-image-placeholder">
                <img src={post.image} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />
              </div>
              <div className="blog-content">
                <span className="blog-category">{post.category}</span>
                <h3 className="blog-title">{post.title}</h3>
                <p className="blog-excerpt">{post.excerpt}</p>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span className="blog-meta">{post.date}</span>
                  <Link to={`/blog/${post.slug}`} className="blog-read-more">
                    Read More <Icons.ArrowRight />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ==========================================
   CONTACT SECTION
   ========================================== */
function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = `New Consultation Request 📋

*Name:* ${formData.name}
*Email:* ${formData.email}
*Phone:* ${formData.phone}
*Practice Area:* ${formData.subject}

*Message:*
${formData.message}`;
    const waUrl = `https://wa.me/916200969522?text=${encodeURIComponent(text)}`;
    window.open(waUrl, '_blank');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  return (
    <section className="section contact-section" id="contact">
      <div className="container">
        <div className="section-header animate-on-scroll">
          <span className="section-label">Get in Touch</span>
          <h2 className="section-title">Book a Consultation</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">
            Take the first step towards resolving your legal matter. Schedule a
            confidential consultation today.
          </p>
        </div>
        <div className="contact-grid">
          <div className="contact-info animate-on-scroll">
            <h3>Let's Discuss Your Case</h3>
            <p>
              Every legal matter is unique. I offer an initial consultation to
              understand your situation and provide clear guidance on the best
              path forward. Your information is always kept strictly
              confidential.
            </p>
            <div className="contact-details">
              <div className="contact-detail-item">
                <div className="contact-detail-icon">
                  <Icons.MapPin />
                </div>
                <div>
                  <div className="contact-detail-label">Office Address</div>
                  <div className="contact-detail-value">
                    62 New Layout, Sitaramdera, Agrico,
                    <br />
                    East Singhbhum, Jamshedpur, Jharkhand – 831009
                  </div>
                </div>
              </div>
              <div className="contact-detail-item">
                <div className="contact-detail-icon">
                  <Icons.Phone />
                </div>
                <div>
                  <div className="contact-detail-label">Phone</div>
                  <div className="contact-detail-value">+91 62009 69522</div>
                </div>
              </div>
              <div className="contact-detail-item">
                <div className="contact-detail-icon">
                  <Icons.Mail />
                </div>
                <div>
                  <div className="contact-detail-label">Email</div>
                  <div className="contact-detail-value">
                    vijita88a@gmail.com
                  </div>
                </div>
              </div>
              <div className="contact-detail-item">
                <div className="contact-detail-icon">
                  <Icons.Clock />
                </div>
                <div>
                  <div className="contact-detail-label">Working Hours</div>
                  <div className="contact-detail-value">
                    Mon – Sat: 10:00 AM – 7:00 PM
                  </div>
                </div>
              </div>
            </div>
          </div>
          <form
            className="contact-form animate-on-scroll"
            onSubmit={handleSubmit}
          >
            <div className="form-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 00000 00000"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="subject">Subject</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a Practice Area</option>
                  <option value="family">Family Law</option>
                  <option value="corporate">Corporate Law</option>
                  <option value="criminal">Criminal Defense</option>
                  <option value="property">Property Law</option>
                  <option value="civil">Civil Litigation</option>
                  <option value="labour">Labour & Employment</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="message">Your Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Briefly describe your legal matter..."
                required
              ></textarea>
            </div>
            <button type="submit" className="btn-primary form-submit">
              Send Message
            </button>
          </form>
        </div>

        {/* Google Maps */}
        <div className="map-wrapper animate-on-scroll">
          <div className="map-header">
            <span className="map-label">📍 Find Our Office</span>
            <a
              href="https://www.google.com/maps/search/Sitaramdera+Agrico+Jamshedpur+Jharkhand"
              target="_blank"
              rel="noopener noreferrer"
              className="map-directions-link"
            >
              Get Directions →
            </a>
          </div>
          <div className="map-frame-wrapper">
            <iframe
              title="Office Location"
              src="https://maps.google.com/maps?q=Sitaramdera%2C+Agrico%2C+East+Singhbhum%2C+Jamshedpur%2C+Jharkhand+831009&output=embed&z=15"
              width="100%"
              height="360"
              style={{ border: 0, display: 'block' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ==========================================
   FOOTER COMPONENT
   ========================================== */
function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div>
            <div className="footer-brand">
              Advocate <span>Vijita Topno</span>
            </div>
            <p className="footer-about">
              Providing exceptional legal representation with integrity,
              dedication, and a commitment to justice. Your trusted legal
              partner for all matters.
            </p>
          </div>
          <div>
            <h4 className="footer-heading">Quick Links</h4>
            <ul className="footer-links">
              <li><a href="#home">Home</a></li>
              <li><a href="#about">About</a></li>
              <li><a href="#practice">Practice Areas</a></li>
              <li><a href="#experience">Experience</a></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
          </div>
          <div>
            <h4 className="footer-heading">Practice Areas</h4>
            <ul className="footer-links">
              <li><a href="#practice">Family Law</a></li>
              <li><a href="#practice">Corporate Law</a></li>
              <li><a href="#practice">Criminal Defense</a></li>
              <li><a href="#practice">Property Law</a></li>
              <li><a href="#practice">Civil Litigation</a></li>
            </ul>
          </div>
          <div>
            <h4 className="footer-heading">Contact Info</h4>
            <ul className="footer-links">
              <li><a href="tel:+916200969522">+91 62009 69522</a></li>
              <li><a href="mailto:vijita88a@gmail.com">vijita88a@gmail.com</a></li>
              <li><a href="#contact">62 New Layout, Sitaramdera, Agrico, Jamshedpur – 831009</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="footer-copyright">
            © {new Date().getFullYear()} Advocate Vijita Topno. All rights reserved.
          </p>
          <div className="footer-social">
            <a href="https://wa.me/916200969522" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
              <Icons.WhatsApp />
            </a>
            <a href="mailto:vijita88a@gmail.com" aria-label="Email">
              <Icons.Mail />
            </a>
            <a href="tel:+916200969522" aria-label="Phone">
              <Icons.Phone />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ==========================================
   MAIN APP COMPONENT
   ========================================== */
function WhatsAppButton() {
  const phone = '916200969522';
  const message = encodeURIComponent('Hello Vijita, I need legal consultation.');
  const url = `https://wa.me/${phone}?text=${message}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-fab"
      aria-label="Chat on WhatsApp"
    >
      <svg viewBox="0 0 32 32" width="28" height="28" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 2C8.268 2 2 8.268 2 16c0 2.52.664 4.883 1.824 6.93L2 30l7.294-1.794A13.94 13.94 0 0 0 16 30c7.732 0 14-6.268 14-14S23.732 2 16 2zm0 25.6a11.55 11.55 0 0 1-5.88-1.604l-.42-.25-4.328 1.064 1.09-4.216-.274-.434A11.56 11.56 0 0 1 4.4 16C4.4 9.592 9.592 4.4 16 4.4c6.408 0 11.6 5.192 11.6 11.6 0 6.408-5.192 11.6-11.6 11.6zm6.348-8.684c-.348-.174-2.06-1.016-2.38-1.132-.32-.116-.552-.174-.784.174-.232.348-.9 1.132-1.104 1.364-.202.232-.406.26-.754.086-.348-.174-1.468-.54-2.796-1.724-1.032-.922-1.73-2.06-1.932-2.408-.202-.348-.022-.536.152-.71.156-.154.348-.406.522-.608.174-.202.232-.348.348-.58.116-.232.058-.436-.028-.61-.088-.174-.784-1.89-1.074-2.588-.284-.68-.572-.588-.784-.598l-.668-.012c-.232 0-.61.086-.928.434-.318.348-1.218 1.19-1.218 2.9 0 1.71 1.246 3.362 1.42 3.594.174.232 2.452 3.742 5.942 5.248.83.358 1.48.572 1.984.732.834.264 1.594.226 2.194.138.67-.1 2.06-.842 2.35-1.656.29-.814.29-1.512.202-1.656-.086-.144-.318-.232-.666-.406z"/>
      </svg>
      <span className="whatsapp-fab-label">Chat with us</span>
    </a>
  );
}

/* ==========================================
   BLOG POST PAGE
   ========================================== */
function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="blog-post-notfound">
        <h2>Post not found.</h2>
        <button onClick={() => navigate('/')} className="btn-primary">Back to Home</button>
      </div>
    );
  }

  return (
    <div className="App style-classic">
      <Navbar />
      <div className="blog-post-page">
        <div className="blog-post-hero">
          <img src={post.image} alt={post.title} className="blog-post-hero-img" onError={e => { e.target.style.display = 'none'; }} />
          <div className="blog-post-hero-overlay">
            <div className="blog-post-inner">
              <span className="blog-category" style={{ marginBottom: '0.75rem', display: 'inline-block' }}>{post.category}</span>
              <h1 className="blog-post-title">{post.title}</h1>
              <p className="blog-post-date">{post.date}</p>
            </div>
          </div>
        </div>
        <div className="blog-post-body">
          <button className="blog-back-btn" onClick={() => navigate('/')}>
            ← Back to Articles
          </button>
          {post.content.map((block, i) => {
            if (block.type === 'paragraph') {
              return <p key={i} className="blog-post-para">{block.text}</p>;
            }
            if (block.type === 'images') {
              return (
                <div key={i} className="blog-post-gallery">
                  {block.images.map((img, j) => (
                    <img key={j} src={img} alt={block.caption} className="blog-post-gallery-img" onError={e => { e.target.style.display = 'none'; }} />
                  ))}
                  {block.caption && <p className="blog-post-gallery-caption">{block.caption}</p>}
                </div>
              );
            }
            return null;
          })}
          <div className="blog-post-footer">
            <p>— Adv. Vijita Topno</p>
            <a href="https://wa.me/916200969522?text=Hello%20Vijita%2C%20I%20read%20your%20article%20and%20need%20legal%20consultation." target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ textDecoration: 'none', display: 'inline-block', marginTop: '1rem' }}>
              Get in Touch on WhatsApp
            </a>
          </div>
        </div>
      </div>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}

/* ==========================================
   MAIN APP
   ========================================== */
function MainApp() {
  useScrollAnimation();
  const [settings, setSettings] = useState({ color: 'brown', style: 'classic', hero: 'split', font: 'serif' });

  const handleSettingChange = (type, value) => {
    const root = document.documentElement;
    if (type === 'color') {
      setSettings(s => ({ ...s, color: value.key }));
      Object.entries(value.vars).forEach(([k, v]) => root.style.setProperty(k, v));
    } else if (type === 'font') {
      setSettings(s => ({ ...s, font: value.key }));
      Object.entries(value.vars).forEach(([k, v]) => root.style.setProperty(k, v));
    } else if (type === 'style') {
      setSettings(s => ({ ...s, style: value }));
    } else if (type === 'hero') {
      setSettings(s => ({ ...s, hero: value }));
    }
  };

  const appClass = `App style-${settings.style} hero-${settings.hero}`;

  return (
    <div className={appClass}>
      <ScrollProgressBar />
      <Navbar />
      <HeroSection />
      <AboutSection />
      <PracticeSection />
      <ExperienceSection />
      <TestimonialsSection />
      <BlogSection />
      <ContactSection />
      <Footer />
      <ThemeSwitcher settings={settings} onSettingChange={handleSettingChange} />
      <WhatsAppButton />
      <BackToTop />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
